// frontend/src/pages/EventDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  IconButton,
  Snackbar,
  alpha,
  ThemeProvider,
  createTheme,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Импорт компонентов
import EventHeader from '../components/Events/EventHeader';
import EventNewsCarousel from '../components/Events/EventNewsCarousel';
import EventInfoBlock from '../components/Events/EventInfoBlock';
import EventSectionsGrid from '../components/Events/EventSectionsGrid';
import EventMaterialsCarousel from '../components/Events/EventMaterialsCarousel';
import EventParticipateModal from '../components/Events/EventParticipateModal';
import EditCoverModal from '../components/Events/EditCoverModal';
import ConferenceSelector from '../components/Events/ConferenceSelector';
import EventProgramModal from '../components/Events/EventProgramModal';
import EditEventModal from '../components/Events/EditEventModal';
import { getEventTypePalette } from '../components/Events/eventTypePalette';

// API
import { eventsAPI, sectionsAPI, participantsAPI, newsAPI, materialsAPI } from '../services/eventsAPI';
import { projectsAPI } from '../services/api';

// Константы для типов и уровней
const EVENT_TYPES = {
  conference: { label: 'Конференция', icon: '🗣️', color: '#4361ee' },
  seminar: { label: 'Семинар', icon: '📚', color: '#3a0ca3' },
  symposium: { label: 'Симпозиум', icon: '🔬', color: '#7209b7' },
  workshop: { label: 'Воркшоп', icon: '🛠️', color: '#f72585' },
  school: { label: 'Школа', icon: '🏫', color: '#4cc9f0' },
  congress: { label: 'Конгресс', icon: '🌍', color: '#4895ef' },
  forum: { label: 'Форум', icon: '🗣️', color: '#560bad' },
  roundtable: { label: 'Круглый стол', icon: '🔄', color: '#b5179e' },
  competition: { label: 'Конкурс', icon: '🏆', color: '#f8961e' },
  festival: { label: 'Фестиваль', icon: '🎪', color: '#f94144' }
};

// Единая цветовая система типов мероприятий — та же, что используется в EventCard.
// background — атмосфера и фоновые линзы; accent — мягкий акцент; ui — элементы интерфейса.

const EVENT_LEVELS = [
  { value: 'international', label: 'Международный', color: '#9C27B0' },
  { value: 'national', label: 'Всероссийский', color: '#2196F3' },
  { value: 'interregional', label: 'Межрегиональный', color: '#4CAF50' },
  { value: 'regional', label: 'Региональный', color: '#FF9800' },
  { value: 'university', label: 'Внутривузовский', color: '#795548' },
  { value: 'interuniversity', label: 'Межвузовский', color: '#FF6B6B' }
];

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isEventModerator, isEventParticipant } = useAuth();
  const baseTheme = useTheme();

  // Состояния
  const [event, setEvent] = useState(null);
  const [sections, setSections] = useState([]);
  const [news, setNews] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialsTotal, setMaterialsTotal] = useState(0);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [likedNews, setLikedNews] = useState({});

  // Модальные окна
  const [participateModalOpen, setParticipateModalOpen] = useState(false);
  const [editCoverModalOpen, setEditCoverModalOpen] = useState(false);
  const [programModalOpen, setProgramModalOpen] = useState(false);
  const [conferenceModalOpen, setConferenceModalOpen] = useState(false);
  const [conferenceLink, setConferenceLink] = useState(null);
  const [conferenceType, setConferenceType] = useState(null);
  const [liveKitRoomName, setLiveKitRoomName] = useState(null);
  const [conferenceAutoJoin, setConferenceAutoJoin] = useState(false);
  const [conferenceForSection, setConferenceForSection] = useState(null);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);

  // Уведомления
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Состояния для карусели обложки
  const [coverImages, setCoverImages] = useState([]);

  // Загрузка данных
  useEffect(() => {
    if (id && id !== 'undefined') {
      fetchEventData();
      fetchUserProjects();
    } else {
      setError('Некорректный ID мероприятия');
      setLoading(false);
    }
  }, [id]);

  // Загрузка материалов после получения event
  useEffect(() => {
    if (event?.id) {
      fetchMaterials();
    }
  }, [event]);

  const fetchEventData = async () => {
    setLoading(true);
    try {
      const eventData = await eventsAPI.getEventById(id);
      setEvent(eventData);

      if (eventData.cover_images && eventData.cover_images.length > 0) {
        setCoverImages(eventData.cover_images);
      }

      setConferenceLink(eventData.conference_link || null);
      setConferenceType(eventData.conference_type || null);
      setLiveKitRoomName(eventData.livekit_room_name || null);

      if (eventData.has_sections) {
        const sectionsData = await sectionsAPI.getSections(id);
        setSections(sectionsData);
      }

      const newsData = await newsAPI.getNews({ event: id });
      setNews(Array.isArray(newsData) ? newsData : []);

      const participantsData = await participantsAPI.getParticipants({ event: id });
      setParticipants(Array.isArray(participantsData) ? participantsData : []);

    } catch (err) {
      console.error('❌ Ошибка загрузки мероприятия:', err);
      setError('Не удалось загрузить данные мероприятия');
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProjects = async () => {
    try {
      const projectsResponse = await projectsAPI.getAll();
      const projectsData = Array.isArray(projectsResponse)
        ? projectsResponse
        : (projectsResponse?.results || []);

      const userProjectsList = projectsData.filter(p =>
        p.owner?.id === user?.id || p.members?.some(m => m.id === user?.id)
      );
      setUserProjects(userProjectsList);
    } catch (err) {
      console.error('❌ Ошибка загрузки проектов пользователя:', err);
    }
  };


const refreshParticipantsFromServer = async () => {
  try {
    console.log('🔄 EventDetailPage: запрашиваем свежие данные участников...');
    const participantsData = await participantsAPI.getParticipants({ event: id });

    console.log('📦 Полученные данные:', participantsData);

    // Убеждаемся, что данные - массив
    const newParticipantsArray = Array.isArray(participantsData)
      ? participantsData
      : (participantsData.results || []);

    console.log('✅ EventDetailPage: получены участники:', newParticipantsArray.length);

    // 🔥 ВАЖНО: выводим первого участника для проверки
    if (newParticipantsArray.length > 0) {
      console.log('🔍 Первый участник:', newParticipantsArray[0]);
      console.log('🔍 Его секция:', newParticipantsArray[0].section);
    }

    // Маппим данные
    const mappedParticipants = newParticipantsArray.map(p => ({
      ...p,
      section: p.section ? {
        id: p.section.id,
        title: p.section.title,
        color: p.section.color,
        description: p.section.description
      } : null
    }));

    setParticipants(mappedParticipants);

    // 🔥 Возвращаем обновленные данные
    return mappedParticipants;

  } catch (err) {
    console.error('❌ Ошибка обновления участников:', err);
    showNotification('Ошибка обновления участников', 'error');
    return [];
  }
};

  const refreshNews = async () => {
    try {
      const newsData = await newsAPI.getNews({ event: id });
      setNews(Array.isArray(newsData) ? newsData : []);
    } catch (err) {
      console.error('❌ Ошибка обновления новостей:', err);
    }
  };

  const handleLikeToggle = async (newsId, e) => {
    e.stopPropagation();

    try {
      const isCurrentlyLiked = likedNews[newsId];

      setLikedNews(prev => ({
        ...prev,
        [newsId]: !isCurrentlyLiked
      }));

      setNews(prevNews =>
        prevNews.map(item => {
          if (item.id === newsId) {
            return {
              ...item,
              likes_count: isCurrentlyLiked
                ? (item.likes_count - 1)
                : (item.likes_count + 1)
            };
          }
          return item;
        })
      );

      if (isCurrentlyLiked) {
        await newsAPI.unlikeNews(newsId);
      } else {
        await newsAPI.likeNews(newsId);
      }

    } catch (err) {
      console.error('❌ Ошибка при лайке:', err);

      setLikedNews(prev => ({
        ...prev,
        [newsId]: !prev[newsId]
      }));

      setNews(prevNews =>
        prevNews.map(item => {
          if (item.id === newsId) {
            return {
              ...item,
              likes_count: likedNews[newsId]
                ? (item.likes_count + 1)
                : (item.likes_count - 1)
            };
          }
          return item;
        })
      );

      showNotification('Не удалось поставить лайк', 'error');
    }
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleParticipate = async (data) => {
    try {
      await participantsAPI.participate(id, data);
      setParticipateModalOpen(false);
      await refreshParticipantsFromServer();
      showNotification('Заявка успешно отправлена!');
    } catch (err) {
      console.error('❌ Ошибка подачи заявки:', err);
      showNotification(err.message || 'Ошибка при подаче заявки', 'error');
    }
  };

  const handleEdit = () => {
    setEditCoverModalOpen(true);
  };

  const handleDelete = async () => {
    if (window.confirm('Вы уверены, что хотите удалить мероприятие?')) {
      try {
        await eventsAPI.deleteEvent(id);
        showNotification('Мероприятие удалено');
        setTimeout(() => navigate('/events'), 1500);
      } catch (err) {
        console.error('❌ Ошибка удаления:', err);
        showNotification('Ошибка при удалении', 'error');
      }
    }
  };

  const handlePublish = async (newStatus) => {
    try {
      // Завершение мероприятия обрабатывается отдельным endpoint'ом backend.
      // Обычный PATCH статуса здесь не подходит: complete/ проверяет права
      // и допустимые переходы жизненного цикла.
      if (newStatus === 'completed') {
        await eventsAPI.completeEvent(id);
        await fetchEventData();
        showNotification('Мероприятие завершено');
        return;
      }

      const updatedEvent = await eventsAPI.updateEventStatus(id, newStatus || 'published');
      setEvent(updatedEvent);
      await fetchEventData();
      showNotification(`Мероприятие ${newStatus === 'published' ? 'опубликовано' : 'обновлено'}`);
    } catch (err) {
      console.error('❌ Ошибка при обновлении статуса:', err);
      showNotification(
        err.response?.data?.error || err.response?.data?.detail || 'Не удалось обновить статус',
        'error'
      );
    }
  };

  const handleCancel = async () => {
    try {
      const updatedEvent = await eventsAPI.updateEventStatus(id, 'cancelled');
      setEvent(updatedEvent);
      await fetchEventData();
      showNotification('Мероприятие отменено');
    } catch (err) {
      console.error('❌ Ошибка при отмене мероприятия:', err);
      showNotification('Не удалось отменить мероприятие', 'error');
    }
  };

  const handleCreateConference = (autoJoin = false) => {
    setConferenceForSection(null);
    setConferenceAutoJoin(autoJoin);
    setConferenceModalOpen(true);
  };

  const handleCreateSectionConference = (sectionId, sectionTitle) => {
    setConferenceForSection({ id: sectionId, title: sectionTitle });
    setConferenceAutoJoin(false);
    setConferenceModalOpen(true);
  };

  const handleJoinConference = () => {
    if (conferenceType === 'livekit') {
      handleCreateConference(true);
    } else if (conferenceLink) {
      window.open(conferenceLink, '_blank');
    }
  };

  const handleJoinSectionConference = (link) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  const handleEndConference = async () => {
    if (!window.confirm('Вы уверены, что хотите завершить конференцию? Ссылка будет удалена.')) {
      return;
    }

    try {
      if (conferenceForSection) {
        await sectionsAPI.clearSectionConferenceLink(conferenceForSection.id);
        setSections(prev => prev.map(s =>
          s.id === conferenceForSection.id
            ? { ...s, conference_link: null }
            : s
        ));
        showNotification('Конференция секции завершена');
      } else {
        await eventsAPI.clearConferenceLink(id);
        setConferenceLink(null);
        setEvent(prev => ({ ...prev, conference_link: null }));
        await fetchEventData();
        showNotification('Конференция мероприятия завершена');
      }
    } catch (err) {
      console.error('❌ Ошибка при завершении конференции:', err);
      showNotification('Не удалось завершить конференцию', 'error');
    }
  };

  const sectionModerators = sections.reduce((acc, section) => {
      if (section.moderators?.some(m => m.id === user?.id)) {
        acc[section.id] = true;
      }
      return acc;
    }, {});
  const handleConferenceCreated = async (conference) => {
    const isLiveKit = conference && typeof conference === 'object';

    if (conferenceForSection) {
      setSections(prev => prev.map(s =>
        s.id === conferenceForSection.id
          ? {
              ...s,
              conference_link: isLiveKit ? null : conference,
              conference_type: isLiveKit ? conference.conference_type : null,
              livekit_room_name: isLiveKit ? conference.livekit_room_name : null,
            }
          : s
      ));
    } else {
      const link = isLiveKit ? null : conference;
      setConferenceLink(link);
      setConferenceType(isLiveKit ? conference.conference_type : null);
      setLiveKitRoomName(isLiveKit ? conference.livekit_room_name : null);
      setEvent(prev => ({
        ...prev,
        conference_link: link,
        conference_type: isLiveKit ? conference.conference_type : null,
        livekit_room_name: isLiveKit ? conference.livekit_room_name : null,
      }));
      await fetchEventData();
    }

    if (conference) {
      showNotification('Конференция создана');
    }
  };

  const handleShowProgram = () => {
    setProgramModalOpen(true);
  };

  const handleShowDocuments = () => {
    showNotification('Документы появятся позже', 'info');
  };

  const handleCoverSaved = (newImages) => {
    setCoverImages(newImages);
    setEvent(prev => ({ ...prev, cover_images: newImages }));
    showNotification('Обложка обновлена');
  };

  const handleProgramUpdate = () => {
    console.log('Программа обновлена');
  };

  const handleEditInfo = () => {
    setEditEventModalOpen(true);
  };

  const handleEventSaved = (updatedEvent) => {
    setEvent(updatedEvent);
    fetchEventData();
    showNotification('Информация обновлена');
  };

  // ЕДИНЫЙ ИСТОЧНИК ПРАВ
  const isCreator = event?.created_by?.id === user?.id;
  const isOrganizer = event?.organizers?.some(org => org.id === user?.id) || false;
  const isPlenaryModerator = event?.plenary_moderators?.some(m => m.id === user?.id) || false;
  const isSectionModerator = sections.some(section =>
    section.moderators?.some(m => m.id === user?.id)
  ) || false;
  const isAnyModerator = isPlenaryModerator || isSectionModerator;

  const canEditAnything = isCreator || isOrganizer || isAnyModerator;
  const canCreateNews = isCreator || isOrganizer || isPlenaryModerator;
  const canEditInfo = isCreator || isOrganizer;

  const isParticipant = isEventParticipant ? isEventParticipant(event) : false;
  const hasPendingRequest = Array.isArray(participants)
    ? participants.some(p => p.user?.id === user?.id && p.status === 'pending')
    : false;

  const typeInfo = event ? EVENT_TYPES[event.type] : null;
  const levelInfo = event ? EVENT_LEVELS.find(l => l.value === event.level) : null;
  const typePalette = getEventTypePalette(event?.type, typeInfo);
  const typeAccent = typePalette.accent;
  const typeBackground = typePalette.background;
  const typeUIAccent = typePalette.ui;

  const eventTheme = React.useMemo(
    () => createTheme(baseTheme, {
      palette: {
        primary: {
          main: typeUIAccent,
          dark: typeUIAccent,
          light: typeAccent,
          contrastText: '#FFFFFF'
        }
      }
    }),
    [baseTheme, typeAccent, typeUIAccent]
  );

// 🔥 ОБНОВЛЕННЫЙ МЕТОД fetchMaterials

const fetchMaterials = async () => {
  if (!event?.id) {
    console.log('❌ Нет event.id');
    return;
  }

  setMaterialsLoading(true);
  setMaterialsError(null);

  try {
    console.log('📡 Загружаем пленарных докладчиков для мероприятия:', event.id);

    // Получаем всех пленарных докладчиков
    const speakersResponse = await participantsAPI.getParticipants({
      event: event.id,
      status: 'approved',
      participation_type: 'speaker',
      is_plenary: true
    });

    const plenarySpeakers = Array.isArray(speakersResponse) ? speakersResponse : [];

    if (plenarySpeakers.length === 0) {
      console.log('ℹ️ Нет пленарных докладчиков');
      setMaterials([]);
      setMaterialsTotal(0);
      setMaterialsLoading(false);
      return;
    }

    console.log(`📡 Загружаем материалы для ${plenarySpeakers.length} докладчиков`);

    // Получаем материалы для этих докладчиков
    const speakerIds = plenarySpeakers.map(s => s.id);
    const materialsResponse = await materialsAPI.getAllMaterialsPaginated(1, 100, {
      participant__in: speakerIds.join(',')
    });

    const materialsList = materialsResponse.results || [];
    console.log('📦 Пример докладчика:', plenarySpeakers[0]);
    console.log('📦 uploaded_files докладчика:', plenarySpeakers[0]?.uploaded_files);
    // Создаем виртуальные папки для всех докладчиков
    const folders = plenarySpeakers.map(speaker => {
      const speakerMaterials = materialsList.filter(m => m.participant === speaker.id);

      console.log(`   - Докладчик ID ${speaker.id}:`, {
        materialsCount: speakerMaterials.length,
        uploadedFilesCount: speaker.uploaded_files?.length || 0,
        hasProject: !!speaker.project
      });

      return {
        id: `speaker-${speaker.id}`,
        participant: speaker,
        files: speakerMaterials.length > 0 ? speakerMaterials[0].files : [],
        materials: speakerMaterials,
        // 🔥 ЯВНО ПЕРЕДАЕМ ВСЕ НУЖНЫЕ ПОЛЯ
        uploaded_files: speaker.uploaded_files || [],
        hasProject: !!(speaker.project)
      };
    });

    console.log(`✅ Создано ${folders.length} папок`);
    setMaterials(folders);
    setMaterialsTotal(folders.length);

  } catch (err) {
    console.error('❌ Ошибка в fetchMaterials:', err);
    setMaterialsError(`Ошибка: ${err.message}`);
  } finally {
    setMaterialsLoading(false);
  }

};

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>Загрузка мероприятия...</Typography>
      </Container>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error || 'Мероприятие не найдено'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/events')}
          sx={{ mt: 2, borderRadius: 2 }}
        >
          К списку мероприятий
        </Button>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={eventTheme}>
      <>
      {/* iOS / Glass фон страницы: цвет автоматически наследуется от типа мероприятия. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          background: `
            radial-gradient(circle at 50% 8%, ${alpha(typeBackground, 0.72)} 0%, transparent 34%),
            linear-gradient(180deg, #f8fafc 0%, #f4f6f9 48%, #f8f9fb 100%)
          `,

          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at 50% 50%, ${alpha('#FFFFFF', 0.30)} 0%, transparent 62%)`,
            pointerEvents: 'none'
          }
        }}
      >
        {/* Большая верхняя стеклянная линза */}
        <Box
          sx={{
            position: 'absolute',
            width: '58vw',
            zIndex: 1,
            height: '23vw',
            minWidth: 560,
            minHeight: 220,
            right: '-8vw',
            top: '4vh',
            borderRadius: '50%',
            transform: 'rotate(-13deg)',
            background: `linear-gradient(118deg,
              ${alpha(typeAccent, 0.08)} 0%,
              ${alpha(typeAccent, 0.24)} 42%,
              ${alpha(typeAccent, 0.14)} 57%,
              ${alpha('#FFFFFF', 0.58)} 73%,
              ${alpha('#FFFFFF', 0.05)} 100%
            )`,
            border: `1px solid ${alpha('#FFFFFF', 0.72)}`,
            boxShadow: `
              0 22px 65px ${alpha(typeAccent, 0.16)},
              inset 0 1px 0 ${alpha('#FFFFFF', 0.92)},
              inset 0 -1px 0 ${alpha(typeAccent, 0.20)}
            `,
            backdropFilter: 'blur(18px) saturate(175%)',
            WebkitBackdropFilter: 'blur(18px) saturate(175%)',
            opacity: 0.98,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '9%',
              top: '10%',
              width: '62%',
              height: '32%',
              borderRadius: '50%',
              background: `linear-gradient(105deg, ${alpha('#FFFFFF', 0.54)}, transparent 75%)`,
              transform: 'rotate(-5deg)',
              filter: 'blur(5px)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '7%',
              right: '7%',
              bottom: '11%',
              height: 1,
              background: `linear-gradient(90deg, transparent, ${alpha('#FFFFFF', 0.68)}, transparent)`,
              transform: 'rotate(-1deg)'
            }
          }}
        />

        {/* Левая вытянутая капсула */}
        <Box
          sx={{
            position: 'absolute',
            width: '35vw',
            zIndex: 1,
            height: '18vw',
            minWidth: 360,
            minHeight: 190,
            left: '-10vw',
            top: '28vh',
            borderRadius: '50%',
            transform: 'rotate(17deg)',
            background: `linear-gradient(132deg,
              ${alpha(typeAccent, 0.26)} 0%,
              ${alpha(typeAccent, 0.13)} 43%,
              ${alpha('#FFFFFF', 0.50)} 72%,
              transparent 100%
            )`,
            border: `1px solid ${alpha('#FFFFFF', 0.68)}`,
            boxShadow: `
              0 24px 62px ${alpha(typeAccent, 0.15)},
              inset 0 1px 0 ${alpha('#FFFFFF', 0.90)},
              inset 0 -1px 0 ${alpha(typeAccent, 0.18)}
            `,
            backdropFilter: 'blur(16px) saturate(175%)',
            WebkitBackdropFilter: 'blur(16px) saturate(175%)',
            opacity: 0.96,
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '72%',
              height: 1,
              right: '8%',
              top: '19%',
              background: `linear-gradient(90deg, transparent, ${alpha('#FFFFFF', 0.72)}, transparent)`,
              transform: 'rotate(-3deg)'
            }
          }}
        />

        {/* Центральная маленькая линза — создаёт слоистость */}
        <Box
          sx={{
            position: 'absolute',
            width: 360,
            zIndex: 1,
            height: 170,
            right: '21%',
            top: '39%',
            borderRadius: '50%',
            transform: 'rotate(-22deg)',
            background: `linear-gradient(115deg,
              ${alpha('#FFFFFF', 0.62)} 0%,
              ${alpha(typeAccent, 0.16)} 36%,
              ${alpha(typeAccent, 0.28)} 64%,
              ${alpha('#FFFFFF', 0.12)} 100%
            )`,
            border: `1px solid ${alpha('#FFFFFF', 0.76)}`,
            boxShadow: `0 16px 48px ${alpha(typeAccent, 0.16)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.95)}`,
            backdropFilter: 'blur(14px) saturate(150%)',
            WebkitBackdropFilter: 'blur(14px) saturate(150%)',
            opacity: 0.92
          }}
        />

        {/* Нижняя широкая стеклянная форма */}
        <Box
          sx={{
            position: 'absolute',
            width: '48vw',
            zIndex: 1,
            height: '17vw',
            minWidth: 500,
            minHeight: 170,
            left: '17%',
            bottom: '-4vh',
            borderRadius: '50%',
            transform: 'rotate(-8deg)',
            background: `linear-gradient(112deg,
              transparent 0%,
              ${alpha(typeAccent, 0.11)} 22%,
              ${alpha(typeAccent, 0.20)} 52%,
              ${alpha('#FFFFFF', 0.48)} 75%,
              transparent 100%
            )`,
            border: `1px solid ${alpha('#FFFFFF', 0.66)}`,
            boxShadow: `0 20px 58px ${alpha(typeAccent, 0.13)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.90)}`,
            backdropFilter: 'blur(18px) saturate(175%)',
            WebkitBackdropFilter: 'blur(18px) saturate(175%)',
            opacity: 0.93,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '14%',
              top: '13%',
              width: '46%',
              height: '30%',
              borderRadius: '50%',
              background: `linear-gradient(100deg, ${alpha('#FFFFFF', 0.50)}, transparent)`,
              filter: 'blur(4px)'
            }
          }}
        />

        {/* Тонкая диагональная «лента» — главный iOS-акцент */}
        <Box
          sx={{
            position: 'absolute',
            width: '72vw',
            zIndex: 1,
            height: 92,
            right: '-16vw',
            top: '61%',
            borderRadius: 999,
            transform: 'rotate(-18deg)',
            background: `linear-gradient(90deg,
              transparent 0%,
              ${alpha(typeAccent, 0.07)} 18%,
              ${alpha(typeAccent, 0.17)} 46%,
              ${alpha('#FFFFFF', 0.40)} 62%,
              transparent 100%
            )`,
            border: `1px solid ${alpha('#FFFFFF', 0.48)}`,
            boxShadow: `0 14px 42px ${alpha(typeAccent, 0.10)}`,
            backdropFilter: 'blur(12px) saturate(140%)',
            WebkitBackdropFilter: 'blur(12px) saturate(140%)',
            opacity: 0.84
          }}
        />

        {/* Очень мягкое цветное свечение под всеми стеклянными формами */}
        <Box
          sx={{
            position: 'absolute',
            width: '70vw',
            zIndex: 0,
            height: '58vw',
            minWidth: 650,
            minHeight: 500,
            right: '-24vw',
            top: '-18vw',
            borderRadius: '50%',
            background: alpha(typeAccent, 0.085),
            filter: 'blur(68px)',
            opacity: 0.84
          }}
        />
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          mt: 2,
          mb: 6,
          position: 'relative',
          zIndex: 1
        }}
      >
      {/* Хлебные крошки */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => navigate('/events')}
          sx={{ borderRadius: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" /> Главная
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/events')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <EventIcon fontSize="small" /> Мероприятия
          </Link>
          <Typography color="text.primary">{event.title}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Шапка мероприятия */}
      <EventHeader
        event={event}
        participants={participants}
        sections={sections}
        isCreator={isCreator}
        isPlenaryModerator={isPlenaryModerator}
        isSectionModerator={isSectionModerator}
        isAnyModerator={isAnyModerator}
        isParticipant={isParticipant}
        hasPendingRequest={hasPendingRequest}
        typeInfo={typeInfo}
        levelInfo={levelInfo}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowProgram={handleShowProgram}
        onShowDocuments={handleShowDocuments}
        coverImages={coverImages}
        onEventUpdate={fetchEventData}
        onParticipantsUpdate={refreshParticipantsFromServer}
        conferenceLink={conferenceLink}
        conferenceType={conferenceType}
        liveKitRoomName={liveKitRoomName}
        onCreateConference={handleCreateConference}
        onJoinConference={handleJoinConference}
        onEndConference={handleEndConference}
        onPublish={handlePublish}
        onCancel={handleCancel}
      />

      {/* НОВОСТИ МЕРОПРИЯТИЯ */}
      <EventNewsCarousel
        news={news}
        eventId={id}
        sections={sections}
        canCreateNews={canCreateNews}
        onNewsUpdate={refreshNews}
        likedNews={likedNews}
        onLikeToggle={handleLikeToggle}
      />

      {/* БЛОК ИНФОРМАЦИИ */}
      <EventInfoBlock
        title="О МЕРОПРИЯТИИ"
        content={event.additional_info}
        icon="info"
        eventId={event.id}
        canEdit={canEditInfo}
        onUpdate={fetchEventData}
      />

      {/* Секции (если есть) */}
      {event.has_sections && sections.length > 0 && (
        <EventSectionsGrid
          sections={sections}
          eventId={id}
          isModerator={isAnyModerator}
          plenaryModerator={isPlenaryModerator}
          sectionModerators={sections.reduce((acc, section) => {
            if (section.moderators?.some(m => m.id === user?.id)) {
              acc[section.id] = true;
            }
            return acc;
          }, {})}
          onCreateConference={handleCreateSectionConference}
          onJoinConference={handleJoinSectionConference}
        />
      )}

      {/* МАТЕРИАЛЫ ПЛЕНАРНЫХ ДОКЛАДЧИКОВ - ВСЕГДА */}
      <EventMaterialsCarousel
        materials={materials}
        eventId={event.id}
        eventType={event.type}
        isPlenary={true}
        totalCount={materialsTotal}
        loading={materialsLoading}
        error={materialsError}
        title="МАТЕРИАЛЫ"
      />

      {/* Модальные окна */}
      <EventParticipateModal
        open={participateModalOpen}
        onClose={() => setParticipateModalOpen(false)}
        eventId={event?.id}
        eventTitle={event?.title}
        eventType={event?.type}
        sections={sections}
        existingParticipant={null}
        mode="create"
        onSuccess={() => {
          fetchEventData();
          refreshParticipantsFromServer();
          setParticipateModalOpen(false);
        }}
      />

      <EditCoverModal
        open={editCoverModalOpen}
        onClose={() => setEditCoverModalOpen(false)}
        eventId={id}
        currentImages={coverImages}
        onSave={handleCoverSaved}
      />

      <ConferenceSelector
        open={conferenceModalOpen}
        onClose={() => setConferenceModalOpen(false)}
        eventId={id}
        sectionId={conferenceForSection?.id}
        eventTitle={event?.title}
        sectionTitle={conferenceForSection?.title}
        onConferenceCreated={handleConferenceCreated}
        conferenceType={conferenceForSection
          ? sections.find(s => s.id === conferenceForSection.id)?.conference_type
          : conferenceType
        }
        liveKitRoomName={conferenceForSection
          ? sections.find(s => s.id === conferenceForSection.id)?.livekit_room_name
          : liveKitRoomName
        }
        autoJoin={conferenceAutoJoin}
        existingLink={conferenceForSection
          ? sections.find(s => s.id === conferenceForSection.id)?.conference_link
          : conferenceLink
        }
      />

      <EventProgramModal
      open={programModalOpen}
      onClose={() => setProgramModalOpen(false)}
      eventId={event?.id}
      eventTitle={event?.title}
      eventStartDate={event?.start_date}
      eventEndDate={event?.end_date}
      sections={sections}
      canEditProgram={canEditAnything}
      onProgramUpdate={handleProgramUpdate}
      userRoles={{
        isCreator,
        isOrganizer,
        isPlenaryModerator,
        isSectionModerator: (sectionId) => {
          return sectionModerators[sectionId] || false;
        },
        sectionModerators: Object.keys(sectionModerators).map(Number)
      }}
    />

      <EditEventModal
        open={editEventModalOpen}
        onClose={() => setEditEventModalOpen(false)}
        event={event}
        onSave={handleEventSaved}
      />

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 300
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
      </>
    </ThemeProvider>
  );
};

export default EventDetailPage;